import {
  ImageStreamEventSchema,
  type ImageGenerationMetadata,
} from '../../src/shared/contracts';

const DEFAULT_IMAGE_SIZE = 1024;
const DEFAULT_IMAGE_STEPS = 4;
const MAX_PROMPT_LENGTH = 4000;
const MAX_STREAM_LINE_CHARACTERS = 32 * 1024 * 1024;

export interface ImageSource {
  blob: Blob;
  height: number;
  width: number;
}

export interface ImagePreview {
  blob: Blob;
  step: number;
  totalSteps: number;
}

export interface ImageStreamResult {
  blob: Blob;
  metadata: ImageGenerationMetadata;
}

interface StreamImageOptions {
  baseUrl: string;
  height?: number;
  onPreview: (preview: ImagePreview) => void;
  prompt: string;
  signal: AbortSignal;
  source?: ImageSource;
  width?: number;
}

function base64ToBlob(base64: string, mediaType: string): Blob {
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    throw new Error('The image service returned invalid image data.');
  }

  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mediaType });
}

function normalizedBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

async function upstreamError(response: Response): Promise<Error> {
  const payload = await response.json().catch(() => null) as unknown;
  if (typeof payload === 'object' && payload !== null && 'detail' in payload) {
    const detail = Reflect.get(payload, 'detail');
    if (typeof detail === 'string') {
      return new Error(detail);
    }
  }
  return new Error(`Image request failed with status ${response.status}.`);
}

export async function consumeImageStream(
  response: Response,
  onPreview: (preview: ImagePreview) => void,
): Promise<ImageStreamResult> {
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.toLowerCase().includes('application/x-ndjson') || !response.body) {
    throw new Error('The image service returned an invalid stream.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const processLine = (rawLine: string): ImageStreamResult | null => {
    const line = rawLine.trim();
    if (!line) {
      return null;
    }
    if (line.length > MAX_STREAM_LINE_CHARACTERS) {
      throw new Error('The image service returned an oversized event.');
    }

    let payload: unknown;
    try {
      payload = JSON.parse(line);
    } catch {
      throw new Error('The image service returned malformed stream data.');
    }

    const parsed = ImageStreamEventSchema.safeParse(payload);
    if (!parsed.success) {
      throw new Error('The image service returned an unknown stream event.');
    }

    if (parsed.data.type === 'error') {
      throw new Error(parsed.data.message);
    }
    if (parsed.data.type === 'preview') {
      onPreview({
        blob: base64ToBlob(parsed.data.image, parsed.data.media_type),
        step: parsed.data.step,
        totalSteps: parsed.data.total_steps,
      });
      return null;
    }
    return {
      blob: base64ToBlob(parsed.data.image, parsed.data.media_type),
      metadata: parsed.data.metadata,
    };
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      if (buffer.length > MAX_STREAM_LINE_CHARACTERS && !buffer.includes('\n')) {
        throw new Error('The image service returned an oversized event.');
      }

      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const result = processLine(line);
        if (result) {
          await reader.cancel();
          return result;
        }
      }

      if (done) {
        const result = processLine(buffer);
        if (result) {
          return result;
        }
        throw new Error('The image stream ended before the final picture arrived.');
      }
    }
  } catch (error) {
    await reader.cancel().catch(() => undefined);
    throw error;
  }
}

export async function streamImage(options: StreamImageOptions): Promise<ImageStreamResult> {
  const prompt = options.prompt.trim();
  if (!prompt || prompt.length > MAX_PROMPT_LENGTH) {
    throw new Error('Image prompts must contain between 1 and 4,000 characters.');
  }

  const width = options.width ?? DEFAULT_IMAGE_SIZE;
  const height = options.height ?? DEFAULT_IMAGE_SIZE;
  const baseUrl = normalizedBaseUrl(options.baseUrl);
  let endpoint: string;
  let requestInit: RequestInit;

  if (options.source) {
    endpoint = `${baseUrl}/v1/images/edits/stream`;
    const body = new FormData();
    body.append('image', options.source.blob, `source.${options.source.blob.type === 'image/png' ? 'png' : 'jpg'}`);
    body.append('prompt', prompt);
    body.append('width', String(width));
    body.append('height', String(height));
    body.append('steps', String(DEFAULT_IMAGE_STEPS));
    requestInit = { method: 'POST', body, signal: options.signal };
  } else {
    endpoint = `${baseUrl}/v1/images/generations/stream`;
    requestInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, width, height, steps: DEFAULT_IMAGE_STEPS }),
      signal: options.signal,
    };
  }

  const response = await fetch(endpoint, requestInit);
  if (!response.ok) {
    throw await upstreamError(response);
  }
  return consumeImageStream(response, options.onPreview);
}
